
import React, { useState, useRef } from 'react';
import { Container, Row, Col, Form, Spinner } from 'react-bootstrap';
import { addProject } from '../../store/slices/project';
import { uploadFile, uploadImage } from '../../utils/helpers/fileupload';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { FaFilePdf } from "react-icons/fa6";
import { toast } from 'react-toastify';
import { FiUploadCloud } from "react-icons/fi";

const AMENITY_OPTIONS = [
    'Gated Community Compound Wall',
    '24 Hrs Security Cabin',
    '30 Feet Black Top Road',
    'CMC Panchayath Water Connection',
    'Over Head Electricity',
    'Solar Street Lights',
    'Drainage System',
    'Swimming Pool',
    'Party Hall',
    'Gym',
    'Badminton Court',
    'CCTV Surveillance',
];

const PROJECT_TYPES = ['Residential Plots', 'Commercial Plots', 'Villas', 'Apartments'];
const STATUS_OPTIONS = ['Ongoing', 'Upcoming', 'Completed'];

const EMPTY_LOCATION = { place: '', distance: '' };

const INITIAL_FORM = {
    projectName: '',
    price: '',
    projectType: '',
    approval: '',
    status: '',
    description: "",
    isActive: true,
    location: { area: '', city: '', state: '', country: 'India', googleMapLink: '' },
    cashbackOffer: {
        enabled: false,
        monthlyCashback: '',
        durationMonths: ''
    },
    referralBonus: { level1: '', level2: '', level3: '' },
    legalDetails: {
        titleClear: false,
        loanAvailable: false,
        loanAmountMin: '',
        loanAmountMax: ''
    },
    amenities: [],
    locationAdvantages: [{ ...EMPTY_LOCATION }],
};

const acceptletter_regex = /^[a-zA-Z\s.,/()-]*$/;
const acceptonly_number = /^\d*\.?\d*$/;
const url_regex = /^https?:\/\/.+$/;

function getAccessToken() {
    return localStorage.getItem('accessToken') || '';
}

function FieldError({ msg }) {
    return <span className="apl-error-msg">{msg || ''}</span>;
}

function UploadBox({
    label,
    required,
    accept,
    hint,
    onChange,
    inputRef,
    dragActive,
    onDragOver,
    onDragLeave,
    onDrop,
    multiple = false,
}) {
    return (
        <Form.Group className="mb-0">
            <label className="apl-label">
                {label}
                {required && <span className="apl-required">*</span>}
            </label>

            <div
                className={`apl-upload-box${dragActive ? ' drag-active' : ''}`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            //  onClick={() => inputRef.current && inputRef.current.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    className="apl-file-input-hidden"
                    accept={accept}
                    multiple={multiple}
                    onChange={onChange}
                />

                <div className="apl-upload-icon">
                    <FiUploadCloud size={32} color="#3b82f6" />
                </div>

                <button type="button" className="apl-browse-btn" onClick={() => inputRef.current?.click()}>
                    Browse
                </button>

                <p className="apl-drop-text">drop a file here</p>
                <p className="apl-hint-text">{hint}</p>
            </div>
        </Form.Group>
    );
}


// 
const validateField = (key, value, form) => {

    const v = String(value ?? '').trim();

    const required = {
        projectName: 'Please enter project name',
        price: 'Please enter price',

        projectType: 'Please select project type',

        approval: 'Please enter approval details',
        status: 'Please select status',
        description: 'Please enter description',

        'location.area': 'Please enter area',
        'location.city': 'Please enter city',
        'location.state': 'Please enter state',
        'location.country': 'Please enter country',
        'cashbackOffer.monthlyCashback': 'Please enter monthly cashback amount',
        'cashbackOffer.durationMonths': 'Please enter duration in months',

        'referralBonus.level1': 'Please enter level 1 bonus',
        'referralBonus.level2': 'Please enter level 2 bonus',
        'referralBonus.level3': 'Please enter level 3 bonus',
        'legalDetails.loanAmountMin': 'Please enter minimum loan amount',
        'legalDetails.loanAmountMax': 'Please enter maximum loan amount',
    };

    if (required[key] && !v) return required[key];

    // map link validation<--
    if (
        key === "location.googleMapLink" &&
        v &&
        !url_regex.test(v)
    ) {
        return "Please enter a valid URL starting with http:// or https://";
    }

    if (key === 'legalDetails.loanAmountMax') {
        const min = Number(form?.legalDetails?.loanAmountMin);
        const max = Number(value);
        if (v && min && max && min > max) {
            return 'Maximum amount should be greater than minimum amount';
        }
    }

    return '';
};

export default function AddProject() {

    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});

    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [imageNames, setImageNames] = useState([]);

    const [brochureFile, setBrochureFile] = useState(null);
    const [brochureName, setBrochureName] = useState('');
    const [brochurePreview, setBrochurePreview] = useState('');

    const [masterPlanFile, setMasterPlanFile] = useState(null);
    const [masterPlanName, setMasterPlanName] = useState('');
    const [masterPlanPreview, setMasterPlanPreview] = useState('');

    const [imgDrag, setImgDrag] = useState(false);
    const [brochureDrag, setBrochureDrag] = useState(false);
    const [masterDrag, setMasterDrag] = useState(false);

    const imgInputRef = useRef(null);
    const brochureInputRef = useRef(null);
    const masterInputRef = useRef(null);

    const [submitLoading, setSubmitLoading] = useState(false);
    const [uploadingText, setUploadingText] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [globalError, setGlobalError] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleChange = (key, value, section = null) => {

        const errorKey = section ? `${section}.${key}` : key;

        setForm((prev) => {
            const next = section
                ? { ...prev, [section]: { ...prev[section], [key]: value } }
                : { ...prev, [key]: value };
            return next;
        });

        const msg = validateField(errorKey, value, { ...form, ...(section ? { [section]: { ...form[section], [key]: value } } : {}) });

        setErrors((prev) => ({ ...prev, [errorKey]: msg }));

    };

    const toggleAmenity = (item) => {
        setForm((prev) => {
            const exists = prev.amenities.includes(item);
            return {
                ...prev,
                amenities: exists
                    ? prev.amenities.filter((a) => a !== item)
                    : [...prev.amenities, item],
            };
        });
        setErrors((prev) => ({ ...prev, amenities: '' }));
    };

    const handleAdvantage = (idx, field, value) => {

        const updated = form.locationAdvantages.map((row, i) =>

            i === idx ? { ...row, [field]: value } : row
        );
        setForm((prev) => ({ ...prev, locationAdvantages: updated }));

        const msg = !value?.trim() ? (field === 'place' ? 'Please enter place name' : 'Please enter distance') : '';

        setErrors((prev) => ({ ...prev, [`la.${idx}.${field}`]: msg }));
    };

    const addAdvantageRow = () =>
        setForm((prev) => ({
            ...prev,
            locationAdvantages: [...prev.locationAdvantages, { ...EMPTY_LOCATION }],
        }));

    const removeAdvantageRow = (idx) =>
        setForm((prev) => ({
            ...prev,
            locationAdvantages: prev.locationAdvantages.filter((_, i) => i !== idx),
        }));

    const processImageFiles = (files) => {

        const valid = files.filter((f) => f.size <= 10 * 1024 * 1024);

        if (valid.length < files.length) {
            setErrors((prev) => ({ ...prev, images: 'Some files exceed 10 MB and were skipped' }));

        }
        else {
            setErrors((prev) => ({ ...prev, images: '' }));
        }

        const previews = valid.map((f) => URL.createObjectURL(f));
        setImageFiles((prev) => [...prev, ...valid]);
        setImagePreviews((prev) => [...prev, ...previews]);
        setImageNames((prev) => [...prev, ...valid.map((f) => f.name)]);
    };

    const handleImagesChange = (e) => {

        processImageFiles(Array.from(e.target.files));
        e.target.value = null;

    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        setImgDrag(false);
        processImageFiles(Array.from(e.dataTransfer.files));
    };

    const removeImage = (idx) => {

        URL.revokeObjectURL(imagePreviews[idx]);
        setImageFiles((p) => p.filter((_, i) => i !== idx));
        setImagePreviews((p) => p.filter((_, i) => i !== idx));
        setImageNames((p) => p.filter((_, i) => i !== idx));

    };

    const processBrochure = (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setErrors((prev) => ({ ...prev, brochure: 'File exceeds 10 MB' }));
            return;
        }
        setErrors((prev) => ({ ...prev, brochure: '' }));
        setBrochureFile(file);
        setBrochureName(file.name);
        setBrochurePreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : '');
    };

    const handleBrochureChange = (e) => {
        processBrochure(e.target.files[0]);
        e.target.value = null;
    };

    const handleBrochureDrop = (e) => {
        e.preventDefault();
        setBrochureDrag(false);
        processBrochure(e.dataTransfer.files[0]);
    };

    const processMasterPlan = (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setErrors((prev) => ({ ...prev, masterPlan: 'File exceeds 10 MB' }));
            return;
        }
        setErrors((prev) => ({ ...prev, masterPlan: '' }));
        setMasterPlanFile(file);
        setMasterPlanName(file.name);
        setMasterPlanPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : '');
    };

    const handleMasterPlanChange = (e) => {
        processMasterPlan(e.target.files[0]);
        e.target.value = null;
    };

    const handleMasterPlanDrop = (e) => {
        e.preventDefault();
        setMasterDrag(false);
        processMasterPlan(e.dataTransfer.files[0]);
    };

    const validate = () => {


        const error = {};

        const basicleverkeys = ['projectName', 'price', 'projectType', 'approval', 'status', 'description'];

        basicleverkeys.forEach((key) => {

            const msg = validateField(key, form[key], form);

            if (msg) error[key] = msg;

        });

        // Location details ------------>
        [
            'area', 'city', 'state', 'country'
        ].forEach((field) => {

            const msg = validateField(`location.${field}`, form.location[field], form);

            if (msg) error[`location.${field}`] = msg;
        });

        const mapMsg = validateField('location.googleMapLink', form.location.googleMapLink, form);

        if (mapMsg) error['location.googleMapLink'] = mapMsg;

        // cashbackoffers -- .
        if (form.cashbackOffer.enabled) {
            ['monthlyCashback', 'durationMonths'].forEach((field) => {
                const msg = validateField(`cashbackOffer.${field}`, form.cashbackOffer[field], form);
                if (msg) error[`cashbackOffer.${field}`] = msg;
            });
        }


        // referralBonus--- >
        [1, 2, 3].forEach((lvl) => {

            const msg = validateField(`referralBonus.level${lvl}`, form.referralBonus[`level${lvl}`], form);


            if (msg) error[`referralBonus.level${lvl}`] = msg;


        });

        const minMsg = validateField('legalDetails.loanAmountMin', form.legalDetails.loanAmountMin, form);

        if (minMsg) error['legalDetails.loanAmountMin'] = minMsg;

        const maxMsg = validateField('legalDetails.loanAmountMax', form.legalDetails.loanAmountMax, form);

        if (maxMsg) error['legalDetails.loanAmountMax'] = maxMsg;


        if (!form.amenities.length) error.amenities = 'Please select at least one amenity';

        form.locationAdvantages.forEach((row, idx) => {

            if (!row.place?.trim()) error[`la.${idx}.place`] = 'Please enter place name';


            if (!row.distance?.trim()) error[`la.${idx}.distance`] = 'Please enter distance';


        });

        if (!imageFiles.length) error.images = 'Please upload at least one project image';

        setErrors(error);
        return Object.keys(error).length === 0;
    };


    // handle submit -- >
    const handleSubmit = async () => {
        setSuccessMsg('');
        setGlobalError('');

        if (!validate()) {
            toast.error("Please fix the validation errors");
            return;
        }

        try {
            setSubmitLoading(true);
            const token = getAccessToken();

            setUploadingText('Uploading images…');
            const imageUrls = [];
            for (const file of imageFiles) {
                const url = await uploadImage(file, token);
                imageUrls.push(url);
            }

            setUploadingText('Uploading brochure…');

            const brochureUrl = brochureFile
                ? await uploadFile(brochureFile, token)
                : null;

            setUploadingText('Uploading master plan…');
            const masterPlanUrl = masterPlanFile
                ? await uploadFile(masterPlanFile, token)
                : null;

            setUploadingText('Saving…');

            const payload = {
                ...form,
                images: imageUrls,
                brochure: brochureUrl,
                masterPlan: masterPlanUrl,
                cashbackOffer: {
                    ...form.cashbackOffer,
                    monthlyCashback: Number(form.cashbackOffer.monthlyCashback),
                    durationMonths: Number(form.cashbackOffer.durationMonths),
                },
                referralBonus: {
                    level1: Number(form.referralBonus.level1),
                    level2: Number(form.referralBonus.level2),
                    level3: Number(form.referralBonus.level3),
                },
                legalDetails: {
                    ...form.legalDetails,
                    loanAmountMin: Number(form.legalDetails.loanAmountMin),
                    loanAmountMax: Number(form.legalDetails.loanAmountMax),
                },
            };

            console.log("Payload", payload);

            const response = await dispatch(addProject(payload));

            if (response?.payload || response?.type?.includes('fulfilled')) {
                setSuccessMsg('Project added successfully!');
                setForm(INITIAL_FORM);
                setImageFiles([]);
                setImagePreviews([]);
                setImageNames([]);
                setBrochureFile(null);
                setBrochureName('');
                setBrochurePreview('');
                setMasterPlanFile(null);
                setMasterPlanName('');
                setMasterPlanPreview('');
            }
            toast.success(successMsg || "Project added successfully!");
            navigate("/admin/projects");
        } catch (err) {
            setGlobalError(err?.message || 'Something went wrong. Please try again.');
            toast.error(err?.message || err || "Something went wrong. Please try again");
            toast.error(globalError);
        } finally {
            setSubmitLoading(false);
            setUploadingText('');
        }
    };

    return (
        <>
            <div className="breadcrumb-bar sticky-top mx-1">
                <div className="container-fluid">
                    <div className="d-flex flex-row align-items-center justify-content-between gap-2 py-2">
                        <div className="breadcrumb-left d-flex align-items-center flex-wrap">
                            <Link to="/admin/home" className="breadcrumb-link active">
                                Home
                            </Link>
                            <span className="breadcrumb-separator mx-2">
                                <i className="bi bi-chevron-right"></i>
                            </span>
                            <Link to="/admin/projects" className="breadcrumb-link active">
                                Projects
                            </Link>
                            <span className="breadcrumb-separator mx-2">
                                <i className="bi bi-chevron-right"></i>
                            </span>
                            <span className="breadcrumb-link inactive">
                                Add Project
                            </span>
                        </div>

                    </div>
                </div>
            </div>

            <div className="ud-page-wrap">
                <Container fluid>
                    <div className="ud-header">
                        <h2 className="ud-main-title">Add Project</h2>

                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Basic Information</div>
                        <Row className="g-3">
                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Project Name</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter project name"
                                        value={form.projectName}
                                        onChange={(e) => handleChange('projectName', e.target.value)}
                                        maxLength={50}
                                    />
                                    <FieldError msg={errors.projectName} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Price (per sq ft)</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter price e.g. 2000"
                                        value={form.price}
                                        onChange={(e) => {

                                            if (
                                                e.target.value && !acceptonly_number.test(e.target.value)) return;

                                            handleChange('price', e.target.value);
                                        }}
                                        maxLength={8}
                                    />
                                    <FieldError msg={errors.price} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Project Type</label>
                                    <select
                                        className="global-input"
                                        value={form.projectType}
                                        onChange={(e) => handleChange('projectType', e.target.value)}
                                    >
                                        <option value="">Select project type</option>
                                        {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <FieldError msg={errors.projectType} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Status</label>
                                    <select
                                        className="global-input"
                                        value={form.status}
                                        onChange={(e) => handleChange('status', e.target.value)}
                                    >
                                        <option value="">Select status</option>
                                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <FieldError msg={errors.status} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={6}>
                                <Form.Group>
                                    <label className="apl-label">Approval Details</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter approval info e.g. DTDC Approved"
                                        value={form.approval}
                                        onChange={(e) => handleChange('approval', e.target.value)}
                                        maxLength={50}
                                    />
                                    <FieldError msg={errors.approval} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3} >
                                <div className="apl-toggle-wrap mt-4">
                                    <input
                                        type="checkbox"
                                        className="apl-toggle"
                                        id="isActive"
                                        checked={form.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)}
                                    />
                                    <label className="apl-toggle-label" htmlFor="isActive">Active Listing</label>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Location Details</div>
                        <Row className="g-3">
                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Area</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter area e.g. Hoskote"
                                        value={form.location.area}
                                        onChange={(e) => handleChange('area', e.target.value, 'location')}
                                        maxLength={40}
                                    />
                                    <FieldError msg={errors['location.area']} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">City</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter city e.g. Bangalore"
                                        value={form.location.city}
                                        onChange={(e) => handleChange('city', e.target.value, 'location')}
                                        maxLength={40}
                                    />
                                    <FieldError msg={errors['location.city']} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">State</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter state e.g. Karnataka"
                                        value={form.location.state}
                                        onChange={(e) => handleChange('state', e.target.value, 'location')}
                                        maxLength={35}
                                    />
                                    <FieldError msg={errors['location.state']} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Country</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter country"
                                        value={form.location.country}
                                        onChange={(e) => handleChange('country', e.target.value, 'location')}
                                        maxLength={25}
                                    />
                                    <FieldError msg={errors['location.country']} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={6}>
                                <Form.Group>
                                    <label className="apl-label">Google Map Link</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter Google Maps URL e.g. https://maps.google.com/..."
                                        value={form.location.googleMapLink}
                                        onChange={(e) => handleChange('googleMapLink', e.target.value, 'location')}
                                    />
                                    <FieldError msg={errors['location.googleMapLink']} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Cashback Offer</div>
                        <Row className="g-3 align-items-start">
                            <Col xs={12} sm={6} lg={3} className="d-flex align-items-center" style={{ paddingTop: 28 }}>
                                <div className="apl-toggle-wrap">
                                    <input
                                        type="checkbox"
                                        className="apl-toggle"

                                        id="cashbackEnabled"
                                        checked={form.cashbackOffer.enabled}
                                        onChange={(e) => handleChange('enabled', e.target.checked, 'cashbackOffer')}
                                    />
                                    <label className="apl-toggle-label" htmlFor="cashbackEnabled">Enable Cashback</label>
                                </div>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Monthly Cashback (₹)</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter amount e.g. 30000"
                                        disabled={!form.cashbackOffer.enabled}
                                        value={form.cashbackOffer.monthlyCashback}
                                        onChange={(e) => {
                                            if (
                                                e.target.value && !acceptonly_number.test(e.target.value))
                                                return;

                                            handleChange('monthlyCashback', e.target.value, 'cashbackOffer');
                                        }}
                                        maxLength={6}
                                    />
                                    <FieldError msg={errors['cashbackOffer.monthlyCashback']} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Duration (Months)</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter months e.g. 60"
                                        disabled={!form.cashbackOffer.enabled}
                                        value={form.cashbackOffer.durationMonths}
                                        onChange={(e) => {
                                            if (
                                                e.target.value && !acceptonly_number.test(e.target.value))
                                                return;

                                            handleChange('durationMonths', e.target.value, 'cashbackOffer');
                                        }}
                                        maxLength={3}
                                    />
                                    <FieldError msg={errors['cashbackOffer.durationMonths']} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* referel bonus */}
                    <div className="global-card">
                        <div className="apl-section-title">Referral Bonus (%)</div>
                        <Row className="g-3">
                            {[1, 2, 3].map((lvl) => (
                                <Col xs={12} sm={6} lg={3} key={lvl}>
                                    <Form.Group>
                                        <label className="apl-label">Level {lvl} Bonus (%)</label>
                                        <input
                                            className="global-input"
                                            placeholder={`Enter level ${lvl} % e.g. ${lvl === 1 ? 10 : lvl === 2 ? 5 : 2}`}
                                            value={form.referralBonus[`level${lvl}`]}
                                            onChange={(e) => {
                                                if (

                                                    e.target.value && !acceptonly_number.test(e.target.value))

                                                    return;
                                                handleChange(`level${lvl}`, e.target.value, 'referralBonus');
                                            }}
                                            maxLength={2}
                                        />
                                        <FieldError msg={errors[`referralBonus.level${lvl}`]} />
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>
                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Legal Details</div>
                        <Row className="g-3 align-items-start">
                            <Col xs={6} sm={4} lg={2} className="d-flex align-items-center" style={{ paddingTop: 28 }}>
                                <div className="apl-toggle-wrap">
                                    <input
                                        type="checkbox"
                                        className="apl-toggle"
                                        id="titleClear"
                                        checked={form.legalDetails.titleClear}
                                        onChange={(e) => handleChange('titleClear', e.target.checked, 'legalDetails')}
                                    />
                                    <label className="apl-toggle-label" htmlFor="titleClear">Title Clear</label>
                                </div>
                            </Col>

                            <Col xs={6} sm={4} lg={2} className="d-flex align-items-center" style={{ paddingTop: 28 }}>
                                <div className="apl-toggle-wrap">
                                    <input
                                        type="checkbox"
                                        className="apl-toggle"
                                        id="loanAvailable"
                                        checked={form.legalDetails.loanAvailable}
                                        onChange={(e) => handleChange('loanAvailable', e.target.checked, 'legalDetails')}
                                    />
                                    <label className="apl-toggle-label" htmlFor="loanAvailable">Loan Available</label>
                                </div>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Min Loan Amount (₹)</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter min loan amount e.g. 2400000"
                                        value={form.legalDetails.loanAmountMin}
                                        onChange={(e) => {
                                            if (e.target.value && !acceptonly_number.test(e.target.value)) return;
                                            handleChange('loanAmountMin', e.target.value, 'legalDetails');
                                        }}
                                        maxLength={10}
                                    />
                                    <FieldError msg={errors['legalDetails.loanAmountMin']} />
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} lg={3}>
                                <Form.Group>
                                    <label className="apl-label">Max Loan Amount (₹)</label>
                                    <input
                                        className="global-input"
                                        placeholder="Enter max loan amount e.g. 3000000"
                                        value={form.legalDetails.loanAmountMax}
                                        onChange={(e) => {
                                            if (e.target.value && !acceptonly_number.test(e.target.value)) return;
                                            handleChange('loanAmountMax', e.target.value, 'legalDetails');
                                        }}
                                        maxLength={10}
                                    />
                                    <FieldError msg={errors['legalDetails.loanAmountMax']} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Amenities</div>
                        <div>
                            {AMENITY_OPTIONS.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    className={`apl-amenity-tag${form.amenities.includes(item) ? ' selected' : ''}`}
                                    onClick={() => toggleAmenity(item)}
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                        <FieldError msg={errors.amenities} />
                    </div>

                    <div className='global-card'>
                        <Col xs={12} sm={12} lg={12}>
                            <Form.Group>

                                <label className="apl-label">Description</label>

                                <textarea
                                    className="global-input"
                                    placeholder="Enter project description"
                                    style={{ height: "6rem" }}
                                    value={form.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    maxLength={500}
                                />
                                <FieldError msg={errors.description} />
                            </Form.Group>
                        </Col>
                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Location Advantages</div>
                        {form.locationAdvantages.map((row, idx) => (

                            <div className="apl-dynamic-row" key={idx}>

                                {form.locationAdvantages.length > 1 && (

                                    <button
                                        type="button"
                                        className="apl-row-remove-btn"
                                        onClick={() => removeAdvantageRow(idx)}
                                        title="Remove row"
                                    > ✕ </button>
                                )}
                                <Row className="g-3">
                                    <Col xs={12} sm={6} lg={4}>
                                        <Form.Group>

                                            <label className="apl-label">Place</label>

                                            <input
                                                className="global-input"
                                                placeholder="Enter place name "
                                                value={row.place}
                                                onChange={(e) => {
                                                    if (e.target.value && !acceptletter_regex.test(e.target.value)) {
                                                        setErrors((prev) => ({ ...prev, [`la.${idx}.place`]: 'Please enter letters only' }));
                                                        return;
                                                    }
                                                    handleAdvantage(idx, 'place', e.target.value);
                                                }}
                                                maxLength={50}
                                            />
                                            <FieldError msg={errors[`la.${idx}.place`]} />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={6} lg={4}>
                                        <Form.Group>
                                            <label className="apl-label">Distance</label>
                                            <input
                                                className="global-input"
                                                placeholder="Enter distance e.g. 3.8 Km"
                                                value={row.distance}
                                                onChange={(e) => handleAdvantage(idx, 'distance', e.target.value)}
                                                maxLength={10}
                                            />
                                            <FieldError msg={errors[`la.${idx}.distance`]} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                        <button type="button" className="apl-add-row-btn" onClick={addAdvantageRow}>
                            + Add
                        </button>
                    </div>

                    <div className="global-card">
                        <div className="apl-section-title">Project Files</div>
                        <Row className="g-4">

                            <Col xs={12}>
                                <label className="apl-label">
                                    Project Images
                                </label>
                                <UploadBox
                                    accept=".jpg,.jpeg,.png,.webp"
                                    hint="*File supported .png, .jpg & .webp"
                                    onChange={handleImagesChange}
                                    inputRef={imgInputRef}
                                    dragActive={imgDrag}
                                    multiple={true}
                                    onDragOver={(e) => { e.preventDefault(); setImgDrag(true); }}
                                    onDragLeave={() => setImgDrag(false)}
                                    onDrop={handleImageDrop}
                                />

                                <FieldError msg={errors.images} />

                                {imagePreviews.length > 0 && (
                                    <div className="apl-img-preview-grid mt-3">
                                        {imagePreviews.map((src, idx) => (
                                            <div className="apl-img-thumb" key={idx}>
                                                <img src={src} alt={imageNames[idx]} />
                                                <button
                                                    type="button"
                                                    className="apl-img-remove-btn"
                                                    onClick={() => removeImage(idx)}
                                                    title="Remove image"
                                                >✕</button>
                                                <div className="apl-img-thumb-name">{imageNames[idx]}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Col>

                            <Col xs={12} sm={6}>
                                <UploadBox
                                    label="Brochure"
                                    multiple={false}
                                    accept=".pdf"
                                    hint="*File supported .pdf"
                                    onChange={handleBrochureChange}
                                    inputRef={brochureInputRef}
                                    dragActive={brochureDrag}
                                    onDragOver={(e) => { e.preventDefault(); setBrochureDrag(true); }}
                                    onDragLeave={() => setBrochureDrag(false)}
                                    onDrop={handleBrochureDrop}
                                />
                                <FieldError msg={errors.brochure} />
                                {brochureFile && (
                                    <div className="apl-single-preview mt-2">
                                        {brochurePreview ? (
                                            <img src={brochurePreview} alt={brochureName} className="apl-single-preview-img" />
                                        ) : (
                                            <div className="apl-pdf-preview">
                                                <FaFilePdf />
                                                <span className="apl-pdf-name">{brochureName}</span>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="apl-single-remove-btn"
                                            onClick={() => { setBrochureFile(null); setBrochureName(''); setBrochurePreview(''); }}
                                        >✕ Remove</button>
                                    </div>
                                )}
                            </Col>

                            <Col xs={12} sm={6}>
                                <UploadBox
                                    label="Master Plan"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    hint="*File supported .png, .jpg & .pdf"
                                    onChange={handleMasterPlanChange}
                                    inputRef={masterInputRef}
                                    dragActive={masterDrag}
                                    onDragOver={(e) => { e.preventDefault(); setMasterDrag(true); }}
                                    onDragLeave={() => setMasterDrag(false)}
                                    onDrop={handleMasterPlanDrop}
                                />
                                <FieldError msg={errors.masterPlan} />
                                {masterPlanFile && (
                                    <div className="apl-single-preview mt-2">
                                        {masterPlanPreview ? (
                                            <img src={masterPlanPreview} alt={masterPlanName} className="apl-single-preview-img" />
                                        ) : (
                                            <div className="apl-pdf-preview">
                                                <FaFilePdf />
                                                <span className="apl-pdf-name">{masterPlanName}</span>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="apl-single-remove-btn"
                                            onClick={() => { setMasterPlanFile(null); setMasterPlanName(''); setMasterPlanPreview(''); }}
                                        >✕ Remove</button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>

                    <div className="d-flex flex-column justify-content-center flex-sm-row align-items-sm-center gap-3 mt-2 mb-4">
                        <div className="action-seciton gap-3 mb-5 ">
                            <button
                                type="button"
                                className="global-cancel-btn"
                                onClick={() => navigate(-1)}
                                disabled={submitLoading}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="global-submit-btn"
                                onClick={handleSubmit}
                                disabled={submitLoading}
                            >
                                {submitLoading ? (
                                    <span className="d-flex align-items-center gap-2">
                                        <Spinner animation="border" size="sm" />
                                        {uploadingText || 'Saving…'}
                                    </span>
                                ) : (
                                    'Submit'
                                )}
                            </button>
                        </div>
                    </div>

                </Container>
            </div>
        </>
    );
}